/** @jsx jsx */
import { jsx, Box, Container, Flex, Grid } from 'theme-ui';
import React, { FunctionComponent } from 'react';

// Types
import { PostDetails } from '../../@codersclan/gatsby-theme-airfleet/@types/post';

// Components
import Text, { TEXT_TYPES } from '../text';
import HeroV3WithForm from '../heroV3WithForm';
import AuthorDescription from '../authorDescription';
import Share from '../share';
import Card from '../card';
import TextFrame from '../textFrame';

// Hooks
import { useWpMetadata } from '../../@codersclan/gatsby-theme-airfleet/hooks/wpMetadata.hook';

// Helpers
import { getCategory, getCtaLabel } from '../../helpers/postHelper';

// Styles
import { baseStyle } from './style';
import { getExcerptToCard } from '../../helpers/general';

interface PostWebinarProps {
  webinar: PostDetails;
}
const PostWebinar: FunctionComponent<PostWebinarProps> = React.memo(
  ({ webinar }) => {
    const {
      titlesSettings: {
        optionsTitles: { webinarSection },
      },
    } = useWpMetadata();
    const relatedContent =
      webinar.templateResource?.sections?.relatedContent?.related ?? [];
    const showRelatedContent =
      relatedContent.length > 0 &&
      relatedContent.some(a => a.postSelector !== null);
    const options = webinar.sectionWebinar?.webinarOptions ?? {};

    return (
      <Container className="webnar-section" sx={baseStyle.mainContainer}>
        {/* Section Hero v3 (with Form) */}
        <HeroV3WithForm
          title={options.heroTitle?.title}
          image={options.heroFeaturedImage?.image}
          customForm={options.customForm}
          pardotUrl={options.heroPardotLink?.url}
          sx={baseStyle.hero}
        />

        {/* Section Webinar Description */}
        <Box sx={baseStyle.webinarContent}>
          <Text type={TEXT_TYPES.H2} sx={baseStyle.webinarContentTitle}>
            {webinar.title}
          </Text>
          <TextFrame
            content={webinar.content}
            sx={baseStyle.webinarContentBody}
          />
        </Box>
        {/* Section: Authors */}
        {webinar.sectionWebinar?.webinarOptions?.bios && (
          <Flex sx={baseStyle.authorsContainer}>
            {(webinar.sectionWebinar?.webinarOptions?.bios ?? []).map(b => (
              <AuthorDescription
                authorImage={b.image}
                authorName={b.title || 'John Doe'}
                authorPosition={b.positionText || 'Unemployee'}
                authorCompany={b.companyText || 'Kenna Security'}
                authorDescription={b.content || 'No comments'}
              />
            ))}
          </Flex>
        )}
        {/* Section: Share */}
        <Share sx={baseStyle.share} />
        {/* Section: Upcoming and On-Demand Webinars */}
        {showRelatedContent && [
          <Text
            type={TEXT_TYPES.H2}
            sx={baseStyle.moreWebinarsTitle}
            key="related_content"
          >
            {webinarSection.webinarLatestContentTitle}
          </Text>,
          <Grid
            columns={[1, 3]}
            sx={baseStyle.moreWebinarsGrid}
            key="related_content_grid"
          >
            {relatedContent.map(({ postSelector: p }) => (
              <Card
                key={p.id}
                image={p.featuredImage?.node}
                category={getCategory(p).name}
                categorySlug={getCategory(p).slug}
                title={p.title}
                content={getExcerptToCard(p)}
                cta={{
                  label: getCtaLabel(p),
                  url: p.uri,
                }}
              />
            ))}
          </Grid>,
        ]}
      </Container>
    );
  },
);

export default PostWebinar;
